import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrialInstitutionItemTemplateCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsTrialInstitutionSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionItemTemplateCategoriesActions from "../../../actions/trialInstitutionItemTemplateCategories";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialInstitutionItemTemplateCategoriesPage.scss";

interface TrialInstitutionItemTemplateCategoriesPageProps {
    formFilter: IFormFilter;
    trialItemTemplateCategorys: Dtos.TrialInstitutionItemTemplateCategory[];
    loadingTrialInstitutionItemTemplateCategorys: boolean;
    loadTrialInstitutionItemTemplateCategorysSuccess: boolean;
    loadTrialInstitutionItemTemplateCategorysFailure: boolean;
    loadTrialInstitutionItemTemplateCategorysErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialInstitutionItemTemplateCategorys: TrialInstitutionItemTemplateCategoriesActions.ITrialInstitutionItemTemplateCategoryClearActionCreator;
    loadTrialInstitutionItemTemplateCategorys: TrialInstitutionItemTemplateCategoriesActions.ITrialInstitutionItemTemplateCategoryLoadActionCreator;
    filter: TrialInstitutionItemTemplateCategoriesActions.ITrialInstitutionItemTemplateCategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialInstitutionItemTemplateCategoriesPage: FC<
    TrialInstitutionItemTemplateCategoriesPageProps
> = ({
    formFilter,
    trialItemTemplateCategorys,
    loadingTrialInstitutionItemTemplateCategorys,
    loadTrialInstitutionItemTemplateCategorysSuccess,
    loadTrialInstitutionItemTemplateCategorysFailure,
    loadTrialInstitutionItemTemplateCategorysErrorCode,
    permissions,
    clearTrialInstitutionItemTemplateCategorys,
    loadTrialInstitutionItemTemplateCategorys,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrialInstitutionItemTemplateCategorys =
        !loadingTrialInstitutionItemTemplateCategorys &&
        loadTrialInstitutionItemTemplateCategorysSuccess;


    // Load trial item template categories
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsTrialInstitutionSubMenu(permissions));
            setBreadcrumbs(TrialInstitutionItemTemplateCategoriesBreadcrumbs());

            loadTrialInstitutionItemTemplateCategorys();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsTrialInstitutionSubMenu,
        permissions,
        setBreadcrumbs,
        TrialInstitutionItemTemplateCategoriesBreadcrumbs,
        loadTrialInstitutionItemTemplateCategorys,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionItemTemplateCategorysFailure) {
            navigate(
                `/error/${loadTrialInstitutionItemTemplateCategorysErrorCode}`
            );
        }
    }, [
        loadTrialInstitutionItemTemplateCategorysFailure,
        loadTrialInstitutionItemTemplateCategorysErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the trial item template categories on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitutionItemTemplateCategorys();
        };
    }, [clearTrialInstitutionItemTemplateCategorys]);

    const createTable = useMemo(() => {
        if (
            !trialItemTemplateCategorys ||
            trialItemTemplateCategorys.length == 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no item trial institution item
                            template categories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            trialItemTemplateCategorys.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialItemTemplateCategorys}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : trialItemTemplateCategorys.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : trialItemTemplateCategorys.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no item trial institution item template categories to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/trialinstitution/itemtemplatecategories/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                            style: {
                                color:
                                    rowInfo &&
                                    rowInfo.original &&
                                    rowInfo.original.active
                                        ? "#212529"
                                        : "#9e9e9e",
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialItemTemplateCategorys,
        DEFAULT_PAGE_SIZE,
        PAGE_SIZE_OPTIONS,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trial Institution Item Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.CategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/trialinstitution/itemtemplatecategories/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialInstitutionItemTemplateCategorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutionItemTemplateCategorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showTrialInstitutionItemTemplateCategorys,
        createTable,
    ]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialInstitutionItemTemplateCategorys}
            permission={
                Dtos.Permission.TrialInstitutionItemTemplateCategoriesView
            }
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionItemTemplateCategoriesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    return {
        formFilter: state.trialInstitutionItemTemplateCategory.formFilter,
        trialItemTemplateCategorys:
            state.trialInstitutionItemTemplateCategory.formFilteredData,
        loadingTrialInstitutionItemTemplateCategorys:
            state.trialInstitutionItemTemplateCategory.loadState &&
            state.trialInstitutionItemTemplateCategory.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionItemTemplateCategorysSuccess:
            state.trialInstitutionItemTemplateCategory.loadState &&
            state.trialInstitutionItemTemplateCategory.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionItemTemplateCategorysFailure:
            state.trialInstitutionItemTemplateCategory.loadState &&
            state.trialInstitutionItemTemplateCategory.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionItemTemplateCategorysErrorCode:
            state.trialInstitutionItemTemplateCategory.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionItemTemplateCategoriesPageProps> => {
    return {
        loadTrialInstitutionItemTemplateCategorys: bindActionCreators(
            TrialInstitutionItemTemplateCategoriesActions.LoadTrialInstitutionItemTemplateCategory,
            dispatch
        ),
        clearTrialInstitutionItemTemplateCategorys: bindActionCreators(
            TrialInstitutionItemTemplateCategoriesActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            TrialInstitutionItemTemplateCategoriesActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionItemTemplateCategoriesPage);
